import { useLogError } from '@/composables/useLogError'
import { useAccountStore } from '@/stores/useAccountStore'
import { defineStore } from 'pinia'
import { computed, reactive, ref } from 'vue'

export const useCurrentReadsStore = defineStore('currentReads', () => {
  const { logError } = useLogError()

  // --- State ---

  const balance = ref(0)
  const toGrid = ref(0)
  const fromGrid = ref(0)
  const fromCommunity = ref(0)
  const fromSupplier = ref(0)
  const toCommunity = ref(0)
  const toSupplier = ref(0)
  const communityConsumable = ref(0)
  const communityShareable = ref(0)
  const dotIndex = ref(0)
  const dotCoordinates = reactive({ positiveX: 0, positiveY: 0, negativeX: 0, negativeY: 0 })

  // Set to true initially — only set false when cube is confirmed unreachable.
  const isCubeReachable = ref(true)
  const measureSource = ref(0)

  // Non-reactive — interval IDs don't need to be observed.
  let backendMeterDataInterval: ReturnType<typeof setInterval> | null = null
  const backendMeterDataIntervalDuration = 10000

  // Constants consumed by Dots.vue animation.
  const updateDotsStepDuration = 400
  const wattsPerDot = 500

  // Counts consecutive connection failures before activating the polling fallback.
  let connectionLostCount = 0

  // --- Computed ---

  const currentRead = computed(() => (toGrid.value > 0 ? toGrid.value : fromGrid.value))
  const currentReadIsConsumption = computed(() => fromGrid.value > 0)
  // 1, 2, 4, 8 are clp, prognoses and reserve
  const isSimulationMeasure = computed(() => measureSource.value < 16)

  // --- Actions ---

  function meterDataReceived(data: { lastMinute: { power: any } }) {
    const current = data.lastMinute.power
    balance.value = current.balance
    toGrid.value = Math.abs(current.toGrid)
    fromGrid.value = Math.abs(current.fromGrid)
    fromCommunity.value = Math.abs(current.fromCommunity)
    fromSupplier.value = Math.abs(current.fromSupplier)
    toCommunity.value = Math.abs(current.toCommunity)
    toSupplier.value = Math.abs(current.toSupplier)
    communityConsumable.value = Math.abs(current.communityConsumable)
    communityShareable.value = Math.abs(current.communityShareable)
    measureSource.value = current.measureSource

    let newDotIndex = Math.round(current.balance / wattsPerDot)
    if (newDotIndex > 5) { newDotIndex = 6 }
    if (newDotIndex < -5) { newDotIndex = -6 }
    dotIndex.value = newDotIndex
  }

  function dotPositionsUpdated(coordinates: typeof dotCoordinates) {
    Object.assign(dotCoordinates, coordinates)
  }

  function onConnectionEstablished() {
    if (backendMeterDataInterval) {
      clearInterval(backendMeterDataInterval)
      backendMeterDataInterval = null
    }
    connectionLostCount = 0
    isCubeReachable.value = true
  }

  function onConnectionLost() {
    if (backendMeterDataInterval || useAccountStore().isDemoUser) {
      // Don't set multiple intervals; demo users have no real socket.
      return
    }

    isCubeReachable.value = false

    if (connectionLostCount++ > 2) {
      // getMeterDataFromBackend is not yet implemented.
      backendMeterDataInterval = setInterval(() => {}, backendMeterDataIntervalDuration)
    }
  }

  // --- Lifecycle ---

  function reset() {
    balance.value = 0
    toGrid.value = 0
    fromGrid.value = 0
    fromCommunity.value = 0
    fromSupplier.value = 0
    toCommunity.value = 0
    toSupplier.value = 0
    communityConsumable.value = 0
    communityShareable.value = 0
    dotIndex.value = 0
    Object.assign(dotCoordinates, { positiveX: 0, positiveY: 0, negativeX: 0, negativeY: 0 })
    isCubeReachable.value = true
    measureSource.value = 0
    if (backendMeterDataInterval) {
      clearInterval(backendMeterDataInterval)
      backendMeterDataInterval = null
    }
    connectionLostCount = 0
  }

  return {
    // State
    balance, toGrid, fromGrid, fromCommunity, fromSupplier,
    toCommunity, toSupplier, communityConsumable, communityShareable,
    dotIndex, dotCoordinates,
    isCubeReachable, measureSource,
    updateDotsStepDuration, wattsPerDot,
    // Computed
    currentRead, currentReadIsConsumption, isSimulationMeasure,
    // Actions
    meterDataReceived, dotPositionsUpdated,
    onConnectionEstablished, onConnectionLost,
    // Lifecycle
    reset,
  }
})
